"use client";

import { rateGate } from "@/app/_module/utils/rateGate";
import { useState } from "react";
import { useRouter } from "nextjs-toploader/app";
import { AlertCircle, CreditCard, X, RefreshCw, AlertTriangle } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { routes } from "@/app/_module/constants/routes";
import SubscriptionRenewalModal from "./SubscriptionRenewalModal";
import { useQuery } from "@tanstack/react-query";
import { subscriptionService } from "@/app/_module/services/subscriptionService";

interface FailedPaymentBannerProps {
  onDismiss?: () => void;
  isDismissed?: boolean;
}

export default function FailedPaymentBanner({
  onDismiss,
  isDismissed = false
}: FailedPaymentBannerProps) {
  const router = useRouter();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  // Fetch active subscription to check payment status
  const { data: subscription, isLoading } = useQuery({
    queryKey: ['active-subscription'],
    queryFn: () => subscriptionService.getMyActiveSubscription(),
    refetchInterval: 60000,
    staleTime: 30000,
  });

  // Also check for overdue area code invoices
  const { data: recurringCharges } = useQuery({
    queryKey: ['recurring-charges-payment-check'],
    // rateGate: one network call per 30 s no matter how often the query is
    // re-run (Safari 26 storm, 2026-09-17); the query itself polls every 5 min.
    queryFn: () => rateGate('recurring-charges-payment-check', 30_000, async () => {
      try {
        const session = (await import("@/app/_module/services/auth")).getSupabaseSession();
        const token = (await session)?.access_token;
        if (!token) return null;
        // Raw fetch — must carry the device id itself, same as the heartbeat.
        // The MFA API gate denies requests without X-Device-Id.
        const { getOrCreateDeviceId } = await import("@/app/_module/services/auth");
        const res = await fetch(
          `${(await import("@/app/_module/services/config")).getApiUrl((await import("@/app/_module/services/endpoints")).endpoint.subscriptions.getRecurringCharges)}`,
          { headers: { Authorization: `Bearer ${token}`, "X-Device-Id": getOrCreateDeviceId() } }
        );
        if (!res.ok) return null;
        const json = await res.json();
        return json?.data;
      } catch { return null; }
    }),
    refetchInterval: 300000, // Check every 5 minutes
    staleTime: 120000,
  });

  if (isDismissed || isLoading) return null;

  // Check if subscription has payment issues
  const isPastDue = subscription?.status === 'past_due';
  const isUnpaid = subscription?.status === 'unpaid';
  const isIncomplete = subscription?.status === 'incomplete';

  // Check for overdue area code charges
  const overdueCharges = recurringCharges?.charges?.filter(
    (c: any) => c.status === 'past_due' || c.status === 'unpaid'
  ) || [];
  const hasOverdueAreaCodes = overdueCharges.length > 0;
  const overdueAmount = overdueCharges.reduce((sum: number, c: any) => sum + (c.amount || 0), 0);

  const hasPaymentIssue = isPastDue || isUnpaid || isIncomplete || hasOverdueAreaCodes;

  if (!hasPaymentIssue) return null;

  const handleUpdatePayment = () => {
    // Route to settings with payment method update
    router.push(routes.settings.path + "?tab=subscription&action=update-payment");
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss?.();
  };

  // Determine banner severity and messaging
  const getBannerConfig = () => {
    if (isUnpaid) {
      return {
        bgColor: "bg-red-50",
        borderColor: "border-red-500",
        textColor: "text-red-800",
        iconColor: "text-red-500",
        buttonColor: "bg-red-600 hover:bg-red-700",
        dismissColor: "text-red-600 hover:text-red-700",
        icon: AlertCircle,
        title: "Payment Failed - Account Locked",
        message: "Your payment could not be processed. Update your payment method immediately to restore access to all features.",
        urgency: "critical"
      };
    }

    if (isPastDue) {
      return {
        bgColor: "bg-orange-50",
        borderColor: "border-orange-500",
        textColor: "text-orange-800",
        iconColor: "text-orange-500",
        buttonColor: "bg-orange-600 hover:bg-orange-700",
        dismissColor: "text-orange-600 hover:text-orange-700",
        icon: AlertTriangle,
        title: "Payment Past Due - Limited Access",
        message: `Your payment is overdue. ${subscription?.nextBillingAt ? `Next retry: ${new Date(subscription.nextBillingAt).toLocaleDateString()}.` : ''} Update your payment method to avoid service interruption.`,
        urgency: "high"
      };
    }

    if (hasOverdueAreaCodes && !isPastDue && !isUnpaid) {
      return {
        bgColor: "bg-orange-50",
        borderColor: "border-orange-500",
        textColor: "text-orange-800",
        iconColor: "text-orange-500",
        buttonColor: "bg-orange-600 hover:bg-orange-700",
        dismissColor: "text-orange-600 hover:text-orange-700",
        icon: AlertTriangle,
        title: "Phone Number Payment Overdue",
        message: `You have ${overdueCharges.length} phone number${overdueCharges.length > 1 ? 's' : ''} with overdue payments${overdueAmount > 0 ? ` ($${(overdueAmount / 100).toFixed(2)}/mo)` : ''}. Update your payment method to keep your numbers active.`,
        urgency: "high"
      };
    }

    // Incomplete payment (default)
    return {
      bgColor: "bg-yellow-50",
      borderColor: "border-yellow-500",
      textColor: "text-yellow-800",
      iconColor: "text-yellow-500",
      buttonColor: "bg-yellow-600 hover:bg-yellow-700",
      dismissColor: "text-yellow-600 hover:text-yellow-700",
      icon: AlertTriangle,
      title: "Payment Incomplete",
      message: "Your subscription payment needs attention. Complete your payment to continue using all features.",
      urgency: "medium"
    };
  };

  const config = getBannerConfig();
  const Icon = config.icon;

  return (
    <>
      <div
        className={`${config.bgColor} border-l-4 ${config.borderColor} p-4 mb-6 shadow-lg`}
        role="alert"
        aria-live="assertive"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            <Icon className={`h-6 w-6 ${config.iconColor} mr-3 flex-shrink-0`} />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className={`text-sm font-bold ${config.textColor}`}>
                  {config.title}
                </h3>
                {config.urgency === "critical" && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white">
                    URGENT
                  </span>
                )}
              </div>
              <p className={`text-sm ${config.textColor} mt-1`}>
                {config.message}
              </p>
              {subscription?.stripeStatus && (
                <p className={`text-xs ${config.textColor} mt-1 opacity-75`}>
                  Status: {subscription.stripeStatus}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2 ml-4">
            <Button
              onClick={handleUpdatePayment}
              size="sm"
              className={`${config.buttonColor} text-white whitespace-nowrap`}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Update Payment
            </Button>
            <Button
              onClick={() => setShowSubscriptionModal(true)}
              variant="outline"
              size="sm"
              className={`${config.textColor} border-current whitespace-nowrap`}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry Payment
            </Button>
            <Button
              onClick={handleDismiss}
              variant="ghost"
              size="sm"
              className={config.dismissColor}
              aria-label="Dismiss notification"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Additional help text for critical issues */}
        {config.urgency === "critical" && (
          <div className={`mt-3 pt-3 border-t ${config.borderColor} border-opacity-30`}>
            <p className={`text-xs ${config.textColor} flex items-center gap-1`}>
              <AlertCircle className="h-3 w-3" />
              Need help? Contact support or update your payment method in Settings → Subscription
            </p>
          </div>
        )}
      </div>

      <SubscriptionRenewalModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onUpgrade={(planId) => {
          router.push(routes.settings.path + "?tab=subscription");
        }}
      />
    </>
  );
}
